import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { toast } from "sonner";
import { Loader2, Gauge, Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SCOPES = [{ k: "azienda", l: "Aziendale" }, { k: "sede", l: "Sede" }, { k: "operatore", l: "Operatore" }];
const PERIODS = [{ k: "mensile", l: "Mensile" }, { k: "trimestrale", l: "Trimestrale" }, { k: "annuale", l: "Annuale" }];

export default function TargetsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/targets"); setRows(data); } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    api.get("/targets/metrics").then(({ data }) => setMetrics(data)).catch(() => {});
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [load, isAdmin]);

  const del = async (id) => { try { await api.delete(`/targets/${id}`); toast.success("Target eliminato"); load(); } catch { toast.error("Errore"); } };

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="targets-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="ds-page-title flex items-center gap-2"><Gauge className="h-6 w-6 text-brand" /> Target</h1>
          <p className="mt-1 text-sm text-slate-500">{isAdmin ? "Obiettivi commerciali configurabili con avanzamento in tempo reale." : "I tuoi obiettivi e l'avanzamento."}</p>
        </div>
        {isAdmin && <Button onClick={() => { setEdit(null); setDlg(true); }} className="gap-1.5" data-testid="new-target-btn"><Plus className="h-4 w-4" /> Nuovo target</Button>}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        : rows.length === 0 ? <div className="ds-card py-12 text-center text-sm text-slate-500" data-testid="targets-empty">Nessun target impostato.</div>
        : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((t) => {
              const pct = t.pct || 0;
              const tone = pct >= 100 ? "bg-success" : pct >= 60 ? "bg-brand" : "bg-warning";
              return (
                <div key={t.id} className="ds-card p-4" data-testid={`target-card-${t.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-slate-800">{t.name}</div>
                      <div className="text-xs text-slate-500">{t.metric_label}</div>
                    </div>
                    <StatusBadge type={pct >= 100 ? "success" : pct >= 60 ? "info" : "warning"} testid={`target-pct-${t.id}`}>{pct}%</StatusBadge>
                  </div>
                  <div className="mt-3 h-2.5 w-full rounded-full bg-slate-100"><div className={`h-2.5 rounded-full ${tone}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-slate-600">Fatto: <strong>{t.actual}</strong></span>
                    <span className="text-slate-600">Target: <strong>{t.target_value}</strong></span>
                    <span className="text-slate-500">Manca: {t.remaining}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                    <span>{SCOPES.find((s) => s.k === t.scope_type)?.l}{t.office_name ? ` · ${t.office_name}` : ""}{t.operator_name ? ` · ${t.operator_name}` : ""}</span>
                    <span>{t.period_start} → {t.period_end}</span>
                  </div>
                  {isAdmin && (
                    <div className="mt-3 flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => { setEdit(t); setDlg(true); }} data-testid={`target-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(t.id)} data-testid={`target-del-${t.id}`}><Trash2 className="h-4 w-4 text-danger" /></Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      {dlg && <TargetDialog open={dlg} onOpenChange={setDlg} target={edit} metrics={metrics} offices={offices} operators={operators} onSaved={load} />}
    </div>
  );
}

function TargetDialog({ open, onOpenChange, target, metrics, offices, operators, onSaved }) {
  const editing = !!target;
  const [f, setF] = useState({
    name: target?.name || "", scope_type: target?.scope_type || "azienda",
    office_id: target?.office_id || "", operator_id: target?.operator_id || "",
    period_type: target?.period_type || "mensile", period_start: target?.period_start || "",
    period_end: target?.period_end || "", metric: target?.metric || "num_vendite",
    target_value: target?.target_value ?? "", active: target?.active ?? true, notes: target?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setError(""); setSaving(true);
    try {
      const payload = { ...f, target_value: Number(f.target_value), office_id: f.office_id || null, operator_id: f.operator_id || null, notes: f.notes || null };
      if (editing) await api.put(`/targets/${target.id}`, payload); else await api.post("/targets", payload);
      toast.success(editing ? "Target aggiornato" : "Target creato"); onSaved(); onOpenChange(false);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="target-dialog">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Modifica target" : "Nuovo target"}</DialogTitle>
          <DialogDescription>Definisci obiettivo e periodo. L'avanzamento è calcolato dai dati reali.</DialogDescription></DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="target-error">{error}</div>}
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nome *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="target-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Ambito</Label>
              <Select value={f.scope_type} onValueChange={(v) => set("scope_type", v)}>
                <SelectTrigger data-testid="target-scope"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{SCOPES.map((s) => <SelectItem key={s.k} value={s.k}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Metrica</Label>
              <Select value={f.metric} onValueChange={(v) => set("metric", v)}>
                <SelectTrigger data-testid="target-metric"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{metrics.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {f.scope_type === "sede" && (
            <div className="space-y-1.5"><Label>Sede *</Label>
              <Select value={f.office_id} onValueChange={(v) => set("office_id", v)}>
                <SelectTrigger data-testid="target-office"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent className="bg-white">{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {f.scope_type === "operatore" && (
            <div className="space-y-1.5"><Label>Operatore *</Label>
              <Select value={f.operator_id} onValueChange={(v) => set("operator_id", v)}>
                <SelectTrigger data-testid="target-operator"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent className="bg-white">{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Periodo</Label>
              <Select value={f.period_type} onValueChange={(v) => set("period_type", v)}>
                <SelectTrigger data-testid="target-period"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PERIODS.map((p) => <SelectItem key={p.k} value={p.k}>{p.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Dal *</Label><Input type="date" value={f.period_start} onChange={(e) => set("period_start", e.target.value)} data-testid="target-from" /></div>
            <div className="space-y-1.5"><Label>Al *</Label><Input type="date" value={f.period_end} onChange={(e) => set("period_end", e.target.value)} data-testid="target-to" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div className="space-y-1.5"><Label>Valore target *</Label><Input type="number" step="0.01" value={f.target_value} onChange={(e) => set("target_value", e.target.value)} data-testid="target-value" /></div>
            <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="target-active" /><Label>Attivo</Label></div>
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button><Button onClick={save} disabled={saving} data-testid="target-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
